import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { GetPasswordRegex, GetBeeNumberAuthenicatedUser } from '../stores/SettingsStore'
import { PwsProfileGeneralGet, PwsProfileGeneralSave, PwsProfileAccountInfoSave } from '../api/AccountInfo'
import type { IPwsProfileGeneralGetResponse, IPwsProfileGeneralSaveRequest } from '../types/AccountInfoTypes'
import type { IPhoneOutput } from '../types/CommonTypes'
import Swal from 'sweetalert2'
import { useFormValidation } from './useFormValidation'
import { handleError } from '../services/ErrorService'
import { createRequiredFieldValidator, createEmailFieldValidator, createNameFieldValidator, getPasswordProhibitedCharMessage } from '../validators/StringValidator'
import { DEFAULT_PASSWORD_REGEX } from '../constants/PasswordPolicy'

export function useContactDetails() {
  const { t } = useI18n()
  const logger = useLogger()

  // Create validators at composable level (same pattern as AoeAddGuestControl)
  const firstNameValidator = [createRequiredFieldValidator(t, 'firstName'), createNameFieldValidator(t, 'firstName')]
  const lastNameValidator = [createRequiredFieldValidator(t, 'lastName'), createNameFieldValidator(t, 'lastName')]
  const emailAddressValidator = [createRequiredFieldValidator(t, 'emailAddress'), createEmailFieldValidator(t, 'emailAddress')]

  // Contact form data
  const contactForm = ref({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    emailAddress: ''
  })

  const { errors: formErrors, setError, clearError, clearErrors } = useFormValidation()

  const isFormValid = computed(() =>
    !!contactForm.value.currentPassword && !formErrors.value.currentPassword &&
    !!contactForm.value.newPassword && !formErrors.value.newPassword &&
    !!contactForm.value.confirmPassword && !formErrors.value.confirmPassword &&
    !formErrors.value.passwordMatch
  )
  const isLoading = ref(false)
  const profileData = ref<IPwsProfileGeneralGetResponse | null>(null)
  const showPasswordPattern = ref<boolean>(false)

  // Phone models for PhoneInputControl (IPhoneOutput format)
  const primaryPhoneModel = ref<IPhoneOutput>({
    number: '',
    countryCode: '',
    formattedNumber: '',
    codeIdPhoneType: '',
    extension: null
  })
  const mobilePhoneModel = ref<IPhoneOutput>({
    number: '',
    countryCode: '',
    formattedNumber: '',
    codeIdPhoneType: '',
    extension: null
  })

  // Password validation functions
  function validateCurrentPassword() {
    clearError('currentPassword')
    if (!contactForm.value.currentPassword) {
      setError('currentPassword', t('accountInfo.contactDetails.currentPasswordRequired'))
      return false
    }
    return true
  }

  async function validateNewPassword() {
    clearError('newPassword')
    showPasswordPattern.value = false

    if (!contactForm.value.newPassword) {
      setError('newPassword', t('accountInfo.contactDetails.newPasswordRequired'))
      return false
    }
    if (contactForm.value.newPassword.length < 8) {
      setError('newPassword', t('accountInfo.contactDetails.newPasswordTooShort'))
      showPasswordPattern.value = true
      return false
    }
    // Check if new password is the same as current password
    if (contactForm.value.currentPassword && contactForm.value.newPassword === contactForm.value.currentPassword) {
      setError('newPassword', t('accountInfo.contactDetails.newPasswordSameAsCurrent'))
      return false
    }
    // Password strength validation using GetPasswordRegex
    let passwordRegexPattern = DEFAULT_PASSWORD_REGEX
    try {
      passwordRegexPattern = await GetPasswordRegex()
    } catch (error) {
      // Fall back to the default password regex if GetPasswordRegex fails
      logger.error('Failed to get password regex:', error)
    }
    const prohibitedCharMessage = getPasswordProhibitedCharMessage(t, passwordRegexPattern, contactForm.value.newPassword)
    if (prohibitedCharMessage) {
      setError('newPassword', prohibitedCharMessage)
      showPasswordPattern.value = true
      return false
    }
    const passwordRegex = new RegExp(passwordRegexPattern)
    if (!passwordRegex.test(contactForm.value.newPassword)) {
      setError('newPassword', t('accountInfo.contactDetails.newPasswordWeak'))
      showPasswordPattern.value = true
      return false
    }
    return true
  }

  function validateConfirmPassword() {
    clearError('confirmPassword')
    clearError('passwordMatch')

    if (!contactForm.value.confirmPassword) {
      setError('confirmPassword', t('accountInfo.contactDetails.confirmPasswordRequired'))
      return false
    }

    if (contactForm.value.newPassword !== contactForm.value.confirmPassword) {
      setError('passwordMatch', t('accountInfo.contactDetails.passwordsDoNotMatch'))
      return false
    }
    return true
  }

  function validateFirstName() {
    clearError('firstName')
    for (const validator of firstNameValidator) {
      const error = validator(contactForm.value.firstName, () => contactForm.value.firstName, { name: 'firstName' })
      if (error) {
        setError('firstName', error)
        return false
      }
    }
    return true
  }

  function validateLastName() {
    clearError('lastName')
    for (const validator of lastNameValidator) {
      const error = validator(contactForm.value.lastName, () => contactForm.value.lastName, { name: 'lastName' })
      if (error) {
        setError('lastName', error)
        return false
      }
    }
    return true
  }

  function validateEmailAddress() {
    clearError('emailAddress')
    for (const validator of emailAddressValidator) {
      const error = validator(contactForm.value.emailAddress, () => contactForm.value.emailAddress, { name: 'emailAddress' })
      if (error) {
        setError('emailAddress', error)
        return false
      }
    }
    return true
  }

  async function validatePasswordForm() {
    const isCurrentValid = validateCurrentPassword()
    const isNewValid = await validateNewPassword()
    const isConfirmValid = validateConfirmPassword()

    return isCurrentValid && isNewValid && isConfirmValid
  }

  async function loadProfileData() {
    try {
      isLoading.value = true
      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) {
        logger.error('No authenticated user bee number found')
        return
      }

      const response = await PwsProfileGeneralGet({ beeNumber })
      logger.debug('Profile response received:', response)

      // Store the complete response
      profileData.value = response

      // Extract the actual data from the nested 'item' property
      const data = response?.item
      logger.debug('Profile data from item:', data)

      if (data) {
        // Log before assignment
        logger.debug('Before form assignment - contactForm:', contactForm.value)

        // Populate form with existing data
        contactForm.value.firstName = data.firstName || ''
        contactForm.value.lastName = data.lastName || ''
        contactForm.value.emailAddress = data.emailAddress || ''

        logger.debug('After basic fields - contactForm:', {
          firstName: contactForm.value.firstName,
          lastName: contactForm.value.lastName,
          emailAddress: contactForm.value.emailAddress
        })

        // Handle phone numbers - map to IPhoneOutput format for PhoneInputControl
        if (data.primaryPhone) {
          primaryPhoneModel.value = {
            number: data.primaryPhone.formattedNumber || data.primaryPhone.number || '',
            countryCode: data.primaryPhone.countryCode || '',
            codeIdPhoneType: data.primaryPhone.codeIdPhoneType || 'HOME',
            extension: (data.primaryPhone as any).extension || null,
            formattedNumber: data.primaryPhone.formattedNumber || data.primaryPhone.number || ''
          }
          logger.debug('Primary phone assigned:', primaryPhoneModel.value)
        }
        if (data.altPhone1) {
          mobilePhoneModel.value = {
            number: data.altPhone1.formattedNumber || data.altPhone1.number || '',
            countryCode: data.altPhone1.countryCode || '',
            codeIdPhoneType: data.altPhone1.codeIdPhoneType || 'CELL',
            extension: (data.altPhone1 as any).extension || null,
            formattedNumber: data.altPhone1.formattedNumber || data.altPhone1.number || ''
          }
          logger.debug('Mobile phone assigned:', mobilePhoneModel.value)
        }

        logger.debug('Final contact form state:', contactForm.value)
      } else {
        logger.warn('No profile data found in response item')
      }
    } catch (error) {
      logger.error('Failed to load profile data:', error)
    } finally {
      isLoading.value = false
    }
  }

  async function handlePasswordChange() {
    if (!(await validatePasswordForm())) {
      return
    }

    try {
      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) {
        formErrors.value.currentPassword = t('accountInfo.contactDetails.passwordChangeError')
        return
      }

      logger.debug('Changing password...')
      const response = await PwsProfileAccountInfoSave({
        beeNumber: beeNumber,
        oldPassword: contactForm.value.currentPassword,
        newPassword: contactForm.value.newPassword
      })

      // Handle empty response as success, or check for explicit success
      if (response?.success || !response || !response.responseStatus?.errorCode) {
        // Reset form on success
        contactForm.value.currentPassword = ''
        contactForm.value.newPassword = ''
        contactForm.value.confirmPassword = ''
        clearErrors()

        Swal.fire({
          title: t('accountInfo.contactDetails.passwordChangeSuccess'),
          icon: 'success',
          confirmButtonText: t('buttons.Okay')
        })
      } else {
        const errorMessage = response?.message || t('accountInfo.contactDetails.passwordChangeError')
        setError('currentPassword', errorMessage)
        Swal.fire({
          title: t('labels.Error'),
          text: errorMessage,
          icon: 'error',
          confirmButtonText: t('buttons.Okay')
        })
      }
    } catch (error) {
      logger.error('Password change failed:', error)
      setError('currentPassword', t('accountInfo.contactDetails.passwordChangeError'))
      await handleError(error, t)
    }
  }

  async function handleAccountDetailsChange() {
    // Validate fields before saving
    const isFirstNameValid = validateFirstName()
    const isLastNameValid = validateLastName()
    const isEmailValid = validateEmailAddress()

    if (!isFirstNameValid || !isLastNameValid || !isEmailValid) {
      return
    }

    try {
      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) {
        logger.error('No authenticated user bee number found')
        return
      }

      isLoading.value = true

      // Build the request object conditionally
      const saveRequest: IPwsProfileGeneralSaveRequest = {
        beeNumber,
        firstName: contactForm.value.firstName,
        lastName: contactForm.value.lastName,
        emailAddress: contactForm.value.emailAddress,
        primaryPhone: primaryPhoneModel.value.number ? {
          number: primaryPhoneModel.value.number,
          countryCode: primaryPhoneModel.value.countryCode,
          codeIdPhoneType: primaryPhoneModel.value.codeIdPhoneType || 'HOME'
        } : undefined
      }

      // Only include mobile phone if a number is provided
      if (mobilePhoneModel.value.number && mobilePhoneModel.value.number.trim() !== '') {
        saveRequest.altPhone1 = {
          number: mobilePhoneModel.value.number,
          countryCode: mobilePhoneModel.value.countryCode,
          codeIdPhoneType: mobilePhoneModel.value.codeIdPhoneType || 'CELL'
        }
      }

      const response = await PwsProfileGeneralSave(saveRequest)

      // Handle empty response as success, or check for explicit success
      if (response?.success || !response || !response.responseStatus?.errorCode) {
        Swal.fire({
          title: t('accountInfo.contactDetails.accountDetailsSuccess'),
          icon: 'success',
          confirmButtonText: t('buttons.Okay')
        })
        // Reload profile data to reflect changes
        await loadProfileData()
      } else {
        Swal.fire({
          title: t('labels.Error'),
          text: response?.message || response?.responseStatus?.message || t('accountInfo.contactDetails.accountDetailsError'),
          icon: 'error',
          confirmButtonText: t('buttons.Okay')
        })
      }
    } catch (error) {
      logger.error('Account details save failed:', error)
      await handleError(error, t)
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    contactForm,
    formErrors,
    isFormValid,
    isLoading,
    profileData,
    showPasswordPattern,
    primaryPhoneModel,
    mobilePhoneModel,

    // Methods
    validateCurrentPassword,
    validateNewPassword,
    validateConfirmPassword,
    validatePasswordForm,
    validateFirstName,
    validateLastName,
    validateEmailAddress,
    loadProfileData,
    handlePasswordChange,
    handleAccountDetailsChange
  }
}
